<script>
  import { onMount, onDestroy, createEventDispatcher, tick } from 'svelte';
  const dispatch = createEventDispatcher();
  const base = '/studentplayground/api/admin/teacher-onboarding/';
  let firstName = '', lastName = '', email = '', calendarMode = 'new', calendarId = '', adminEmail = '';
  let sendEmail = false, busy = false, error = '', notice = '', available = false;
  let jobs = [], selected = null, message = '', expiry = '', search = '', page = 1, hasMore = false;
  let confirmed = false, recoveredId = '', acknowledgeUnknown = false;
  let pollTimer, destroyed = false, pollBusy = false;
  let createRequest = null;
  let preview = null, previewDocument = '', previewDialog;
  const announced = new Set();
  const pendingActions = new Map();

  function identity() { return crypto.randomUUID(); }
  function closePreview() {
    previewDialog?.close();
    preview = null; previewDocument = '';
  }
  function browserDocument(data) {
    const doc = new DOMParser().parseFromString(data.html_body, 'text/html');
    doc.querySelectorAll('script,iframe,object,embed,form,base,link,meta[http-equiv]').forEach(node => node.remove());
    doc.querySelectorAll('*').forEach(node => {
      for (const attribute of [...node.attributes]) {
        if (attribute.name.startsWith('on') || ['href', 'src', 'srcset', 'action'].includes(attribute.name)) node.removeAttribute(attribute.name);
      }
    });
    doc.querySelectorAll('img').forEach(node => { if (data.logo_data_url?.startsWith('data:image/png;base64,')) node.setAttribute('src', data.logo_data_url); });
    const policy = doc.createElement('meta');
    policy.httpEquiv = 'Content-Security-Policy';
    policy.content = "default-src 'none'; img-src data:; style-src 'unsafe-inline'; form-action 'none'; base-uri 'none'";
    doc.head.prepend(policy);
    return '<!DOCTYPE html>' + doc.documentElement.outerHTML;
  }
  async function openEmailPreview() {
    if (!selected || busy) return;
    const id = selected.id;
    busy = true; error = ''; notice = '';
    try {
      const data = await api(`${id}/email-preview/`, {}, identity());
      if (destroyed || selected?.id !== id) return;
      previewDocument = browserDocument(data); preview = data;
      await tick();
      previewDialog?.showModal();
    } catch (e) { error = e.message; }
    finally { busy = false; }
  }
  function openTeacher(event) {
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    dispatch('openTeacher', selected);
  }
  async function api(path, body, key) {
    const token = localStorage.getItem('jwtToken');
    if (!token) throw new Error('Please sign in as an administrator.');
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(base + path, { method: body === undefined ? 'GET' : 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', ...(key ? { 'Idempotency-Key': key } : {}) },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }), signal: controller.signal, cache: 'no-store' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const fields = Object.entries(data.fields || {}).map(([field, errors]) => `${field}: ${[].concat(errors).join(' ')}`).join(' ');
        throw new Error(fields || data.detail || data.explanation || data.error || 'Request failed. Please retry.');
      }
      return data;
    } finally { clearTimeout(timeout); }
  }
  function choose(job) {
    if (selected?.id !== job.id) { closePreview(); message = ''; expiry = ''; confirmed = false; acknowledgeUnknown = false; }
    selected = job;
    if (job.teacher_id && !announced.has(job.id)) {
      announced.add(job.id);
      dispatch('created', job);
    }
    if (job.status === 'queued' || job.status === 'running') schedulePoll();
  }
  function schedulePoll() {
    clearTimeout(pollTimer);
    if (!destroyed) pollTimer = setTimeout(poll, 2000);
  }
  async function poll() {
    if (!selected || destroyed || pollBusy) return;
    pollBusy = true;
    const id = selected.id;
    try { const job = await api(`${id}/`); if (!destroyed && selected?.id === id) choose(job); }
    catch (e) { error = e.message; schedulePoll(); }
    finally { pollBusy = false; }
  }
  async function history(reset = true) {
    error = '';
    try {
      const nextPage = reset ? 1 : page + 1;
      const data = await api(`?q=${encodeURIComponent(search)}&page=${nextPage}`);
      if (destroyed) return;
      available = data.enabled;
      jobs = reset ? data.results : [...jobs, ...data.results]; page = nextPage; hasMore = data.has_more;
      if (reset && !selected) {
        const pending = jobs.find(j => j.status === 'queued' || j.status === 'running');
        if (pending) choose(pending);
      }
    } catch (e) { error = e.message; }
  }
  async function submit() {
    busy = true; error = ''; notice = '';
    const body = { first_name: firstName, last_name: lastName, email, calendar_mode: calendarMode,
      calendar_id: calendarMode === 'existing' ? calendarId : '', admin_calendar_email: adminEmail, send_email: sendEmail };
    const fingerprint = JSON.stringify(body);
    if (!createRequest || createRequest.fingerprint !== fingerprint) createRequest = { fingerprint, key: identity() };
    try {
      sessionStorage.setItem('teacherOnboarding:pending', JSON.stringify(createRequest));
      choose(await api('', body, createRequest.key));
      createRequest = null; sessionStorage.removeItem('teacherOnboarding:pending');
      notice = 'Teacher onboarding queued. Progress is saved if you leave this page.';
      await history();
    } catch (e) { error = e.name === 'AbortError' ? 'Request timed out. Retry this submission to check its saved result.' : e.message; }
    finally { busy = false; }
  }
  async function action(name, body = {}) {
    if (!selected) return;
    busy = true; error = ''; notice = '';
    const operation = `${selected.id}/${name}/${JSON.stringify(body)}`;
    if (!pendingActions.has(operation)) pendingActions.set(operation, identity());
    try {
      const data = await api(`${selected.id}/${name}/`, body, pendingActions.get(operation));
      pendingActions.delete(operation);
      if (name === 'message') {
        message = data.message; expiry = data.expires_at;
        try { await navigator.clipboard.writeText(message); notice = 'Message copied.'; }
        catch { notice = 'Select and copy the message below. Clipboard access is unavailable.'; }
      } else { if (name === 'send-email') closePreview(); choose(data); await history(); }
    } catch (e) { error = e.message; }
    finally { busy = false; }
  }
  onMount(() => {
    try { createRequest = JSON.parse(sessionStorage.getItem('teacherOnboarding:pending') || 'null'); } catch { /* ignore invalid local state */ }
    history();
  });
  onDestroy(() => { destroyed = true; clearTimeout(pollTimer); message = ''; preview = null; previewDocument = ''; });
</script>

<section class="onboarding" aria-label="Create teacher">
  <h3>Create teacher</h3>
  {#if !available}<p>Teacher creation is currently disabled. Saved onboarding history remains available.</p>{/if}
  <form on:submit|preventDefault={submit}>
    <div class="fields">
      <label>Teacher first name<input bind:value={firstName} required maxlength="150" autocomplete="given-name" /></label>
      <label>Teacher surname<input bind:value={lastName} required maxlength="150" autocomplete="family-name" /></label>
      <label>Teacher email<input type="email" bind:value={email} required maxlength="254" autocomplete="email" /></label>
    </div>
    <fieldset><legend>Lesson calendar</legend>
      <label class="option"><input type="radio" bind:group={calendarMode} value="new" />Create new calendar</label>
      <label class="option"><input type="radio" bind:group={calendarMode} value="existing" />Connect existing calendar</label>
      {#if calendarMode === 'existing'}
        <label>Existing Google Calendar ID<input bind:value={calendarId} required maxlength="255" /></label>
        <p>The service account needs permission to edit events. The calendar name must end with “ Tutory”.</p>
      {:else}
        <p>Calendar: {firstName || 'First name'} {lastName || 'Surname'} Tutory · Europe/Vilnius</p>
        <p>New calendars are automatically shared with erika.tutory@gmail.com and nojusmar@gmail.com as event editors.</p>
      {/if}
      <label>Administrator Google email (optional)<input type="email" bind:value={adminEmail} maxlength="254" /></label>
      <p>The teacher and optional administrator can edit calendar events.</p>
    </fieldset>
    <label class="option"><input type="checkbox" bind:checked={sendEmail} />Send onboarding email</label>
    <p>The personalized message is also available to copy. Google may send a separate calendar-sharing invitation.</p>
    <button class="primary" type="submit" disabled={busy || !available}>{busy ? 'Working…' : 'Create teacher account'}</button>
  </form>
  {#if error}<p role="alert" class="error">{error}</p>{/if}
  {#if notice}<p role="status" class="notice">{notice}</p>{/if}
  {#if selected}
    <section class="progress" aria-label="Teacher onboarding progress">
      <h4>{selected.first_name} {selected.last_name}</h4>
      <p>{selected.email} · {selected.status.replaceAll('_', ' ')}</p>
      <dl>
        <div><dt>Calendar</dt><dd>{selected.calendar_status}</dd></div>
        <div><dt>Sharing</dt><dd>{selected.sharing_status}</dd></div>
        <div><dt>Account</dt><dd>{selected.account_status}</dd></div>
        <div><dt>Email</dt><dd>{selected.email_status === 'sent' ? 'Sent (accepted by mail server)' : selected.email_status.replaceAll('_', ' ')}</dd></div>
      </dl>
      {#if selected.username}<p>Username: <strong>{selected.username}</strong></p>{/if}
      {#if selected.calendar_name}<p>{selected.calendar_name}</p>{/if}
      {#if selected.calendar_id}<p class="calendar-id">{selected.calendar_id}</p>{/if}
      {#if selected.explanation}<p>{selected.explanation}</p>{/if}
      {#if selected.credential_status === 'selected'}<p>Password already selected. Use Forgot password for account recovery.</p>{/if}
      <div class="actions">
        {#if selected.teacher_id}<a href={`#/admin/teachers?teacherId=${selected.teacher_id}`} on:click={openTeacher}>Open teacher</a>{/if}
        {#if selected.allowed_actions.includes('retry')}<button disabled={busy} on:click={() => action('retry')}>Retry failed step</button>{/if}
        {#if selected.allowed_actions.includes('message')}
          <button disabled={busy} on:click={() => action('message')}>Copy message</button>
          {#if error.includes('expired')}<button disabled={busy} on:click={() => action('message', { renew: true })}>Generate new link</button>{/if}
        {/if}
        {#if selected.allowed_actions.includes('send-email')}
          <button disabled={busy} on:click={openEmailPreview}>Send email</button>
        {/if}
      </div>
      {#if selected.allowed_actions.includes('confirm-sharing')}
        <label class="option"><input type="checkbox" bind:checked={confirmed} />I confirmed calendar sharing in Google Calendar</label>
        <button disabled={busy || !confirmed} on:click={() => action('confirm-sharing', { confirmed: true })}>Confirm sharing</button>
      {/if}
      {#if selected.allowed_actions.includes('recover-calendar')}
        <label>Created calendar ID<input bind:value={recoveredId} /></label>
        <button disabled={busy || !recoveredId.trim()} on:click={() => action('recover-calendar', { calendar_id: recoveredId })}>Recover calendar</button>
      {/if}
      {#if message}
        <p>Link expires: {new Date(expiry).toLocaleString('lt-LT', { timeZone: 'Europe/Vilnius' })} (Lithuanian time)</p>
        <label>Onboarding message<textarea readonly rows="12" value={message}></textarea></label>
      {/if}
    </section>
  {/if}
  <section class="history" aria-label="Teacher onboarding history">
    <h4>Recent teacher onboarding</h4>
    <form class="actions" on:submit|preventDefault={() => history()}>
      <label>Search teacher onboarding<input type="search" bind:value={search} maxlength="150" /></label>
      <button type="submit">Refresh onboarding history</button>
    </form>
    {#each jobs as job (job.id)}
      <button class="history-job" on:click={() => choose(job)}>{job.first_name} {job.last_name} · {job.email} · {job.status.replaceAll('_', ' ')}</button>
    {:else}<p>No matching onboarding records.</p>{/each}
    {#if hasMore}<button on:click={() => history(false)}>Load more onboarding records</button>{/if}
  </section>
</section>

{#if preview}
  <dialog bind:this={previewDialog} aria-label="Teacher onboarding email preview" on:close={closePreview}>
    <h3>Teacher onboarding email preview</h3>
    <dl>
      <div><dt>To</dt><dd>{preview.recipient}</dd></div>
      <div><dt>From</dt><dd>{preview.from_email}</dd></div>
      <div><dt>Subject</dt><dd>{preview.subject}</dd></div>
      <div><dt>Link expires</dt><dd>{new Date(preview.expires_at).toLocaleString('lt-LT', { timeZone: 'Europe/Vilnius' })} (Lithuanian time)</dd></div>
    </dl>
    <iframe title="Email body preview" sandbox="" referrerpolicy="no-referrer" srcdoc={previewDocument}></iframe>
    <p>This is the rendered email. Links are disabled in this preview. Nothing is sent until you confirm.</p>
    {#if selected?.email_status === 'unknown'}<label class="option"><input type="checkbox" bind:checked={acknowledgeUnknown} />I understand the previous email may already have been sent</label>{/if}
    {#if error}<p role="alert" class="error">{error}</p>{/if}
    <div class="actions">
      <button disabled={busy || (selected?.email_status === 'unknown' && !acknowledgeUnknown)} class="primary" on:click={() => action('send-email', { acknowledge_unknown: acknowledgeUnknown, preview_fingerprint: preview.preview_fingerprint })}>Confirm send email</button>
      <button disabled={busy} on:click={closePreview}>Cancel</button>
    </div>
  </dialog>
{/if}

<style>
  dialog { box-sizing: border-box; width: min(850px, 96vw); max-height: 92dvh; overflow: auto; border: 1px solid #b9c9c2; border-radius: 8px; padding: 1.2rem; color: #263b35; text-align: left; }
  dialog::backdrop { background: #0008; }
  iframe { width: 100%; height: 50dvh; border: 1px solid #d7e1dc; background: white; }
  .onboarding { color: #263b35; text-align: left; }
  h3, h4 { margin: 0 0 1rem; }
  .fields { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 180px), 1fr)); gap: 1rem; }
  label { display: flex; flex-direction: column; gap: .4rem; margin-bottom: .9rem; font-weight: 600; }
  input, textarea { box-sizing: border-box; max-width: 100%; width: 100%; border: 1px solid #b9c9c2; padding: .65rem; border-radius: 5px; font: inherit; }
  .option { flex-direction: row; align-items: center; gap: .6rem; }
  .option input { width: auto; flex-shrink: 0; }
  fieldset { border: 1px solid #d7e1dc; border-radius: 6px; margin: .8rem 0 1rem; min-width: 0; padding: 1rem; }
  p { font-size: .9rem; line-height: 1.5; }
  button, a { cursor: pointer; font: inherit; padding: .6rem .8rem; border-radius: 5px; border: 1px solid #a5bcb0; background: #f5f8f6; color: #164d39; }
  .primary { background: #14695b; color: white; }
  button:disabled { opacity: .5; cursor: default; }
  .actions { display: flex; flex-wrap: wrap; gap: .65rem; align-items: center; margin: .8rem 0; }
  .progress, .history { border-top: 1px solid #d7e1dc; padding-top: 1.2rem; margin-top: 1.5rem; }
  dl { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: .7rem; }
  dt { font-weight: 600; } dd { margin: .2rem 0; overflow-wrap: anywhere; }
  .calendar-id { overflow-wrap: anywhere; }
  .history-job { display: block; width: 100%; text-align: left; margin-top: .5rem; overflow-wrap: anywhere; }
  .error { color: #a42525; background: #fff1f1; padding: .7rem; }
  .notice { color: #155237; background: #edf7f0; padding: .7rem; }
  textarea { resize: vertical; font-weight: normal; }
</style>
